import { useEffect, useState } from 'react';

interface PreviewProps {
  tenantId: string;
  tenantName?: string;
  templateDetails: {
    id: string;
    name: string;
    description: string | null;
    isActive: boolean;
    htmlContent?: string;
    fontFamily?: string;
    themeColor?: string;
    backgroundColor?: string;
    logoUrl?: string;
  };
}

const Preview = ({ tenantId, tenantName, templateDetails }: PreviewProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const processHtmlWithTenantId = (html: string) => {
    const patterns = [
      {
        pattern: /const\s+TENANT_ID\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `const TENANT_ID = '${tenantId}';`,
      },
      {
        pattern: /let\s+TENANT_ID\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `let TENANT_ID = '${tenantId}';`,
      },
      {
        pattern: /var\s+TENANT_ID\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `var TENANT_ID = '${tenantId}';`,
      },
      {
        pattern: /TENANT_ID\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `TENANT_ID = '${tenantId}';`,
      },
    ];

    let processed = html;
    patterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(processed)) {
        processed = processed.replace(pattern, replacement);
      }
    });

    return processed;
  };

  const processHtmlWithFontFamily = (html: string) => {
    const patterns = [
      {
        pattern: /const\s+FONT_FAMILY\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `const FONT_FAMILY = '${
          templateDetails.fontFamily || 'DM Sans, sans-serif'
        }';`,
      },
      {
        pattern: /let\s+FONT_FAMILY\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `let FONT_FAMILY = '${
          templateDetails.fontFamily || 'DM Sans, sans-serif'
        }';`,
      },
      {
        pattern: /var\s+FONT_FAMILY\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `var FONT_FAMILY = '${
          templateDetails.fontFamily || 'DM Sans, sans-serif'
        }';`,
      },
      {
        pattern: /FONT_FAMILY\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `FONT_FAMILY = '${
          templateDetails.fontFamily || 'DM Sans, sans-serif'
        }';`,
      },
    ];

    let processed = html;
    patterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(processed)) {
        processed = processed.replace(pattern, replacement);
      }
    });

    return processed;
  };

  const processHtmlWithThemeColor = (html: string) => {
    const patterns = [
      {
        pattern: /const\s+PRIMARY_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `const PRIMARY_COLOR = '${
          templateDetails.themeColor || '#3b82f6'
        }';`,
      },
      {
        pattern: /let\s+PRIMARY_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `let PRIMARY_COLOR = '${
          templateDetails.themeColor || '#3b82f6'
        }';`,
      },
      {
        pattern: /var\s+PRIMARY_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `var PRIMARY_COLOR = '${
          templateDetails.themeColor || '#3b82f6'
        }';`,
      },
      {
        pattern: /PRIMARY_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `PRIMARY_COLOR = '${
          templateDetails.themeColor || '#3b82f6'
        }';`,
      },
    ];

    let processed = html;
    patterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(processed)) {
        processed = processed.replace(pattern, replacement);
      }
    });

    return processed;
  };

  const processHtmlWithBackgroundColor = (html: string) => {
    const patterns = [
      {
        pattern: /const\s+BACKGROUND_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `const BACKGROUND_COLOR = '${
          templateDetails.backgroundColor || '#f8fafc'
        }';`,
      },
      {
        pattern: /let\s+BACKGROUND_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `let BACKGROUND_COLOR = '${
          templateDetails.backgroundColor || '#f8fafc'
        }';`,
      },
      {
        pattern: /var\s+BACKGROUND_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `var BACKGROUND_COLOR = '${
          templateDetails.backgroundColor || '#f8fafc'
        }';`,
      },
      {
        pattern: /BACKGROUND_COLOR\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `BACKGROUND_COLOR = '${
          templateDetails.backgroundColor || '#f8fafc'
        }';`,
      },
    ];

    let processed = html;
    patterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(processed)) {
        processed = processed.replace(pattern, replacement);
      }
    });

    return processed;
  };

  const processHtmlWithRestaurantName = (html: string) => {
    const patterns = [
      {
        pattern: /const\s+STORE_NAME\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `const STORE_NAME = '${tenantName || 'Restaurant Name'}';`,
      },
      {
        pattern: /let\s+STORE_NAME\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `let STORE_NAME = '${tenantName || 'Restaurant Name'}';`,
      },
      {
        pattern: /var\s+STORE_NAME\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `var STORE_NAME = '${tenantName || 'Restaurant Name'}';`,
      },
      {
        pattern: /STORE_NAME\s*=\s*['"][^'"]*['"]\s*;/g,
        replacement: `STORE_NAME = '${tenantName || 'Restaurant Name'}';`,
      },
    ];

    let processed = html;
    patterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(processed)) {
        processed = processed.replace(pattern, replacement);
      }
    });

    return processed;
  };

  const getProcessedContent = () => {
    let htmlContent = templateDetails?.htmlContent || '';

    if (htmlContent) {
      htmlContent = processHtmlWithTenantId(htmlContent);
      htmlContent = processHtmlWithFontFamily(htmlContent);
      htmlContent = processHtmlWithThemeColor(htmlContent);
      htmlContent = processHtmlWithBackgroundColor(htmlContent);
      htmlContent = processHtmlWithRestaurantName(htmlContent);
    }

    return htmlContent;
  };

  if (!templateDetails?.htmlContent) {
    return (
      <div className="w-full h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-2xl">📱</span>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              No template content
            </h3>
            <p className="text-sm text-gray-500 max-w-xs">
              Template content is not available
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen bg-white">
      <iframe
        srcDoc={getProcessedContent()}
        className="w-full h-full border-0"
        title="Digital Menu"
        sandbox="allow-scripts allow-same-origin"
      />
      <button
        type="button"
        onClick={toggleFullscreen}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white/80 hover:bg-white shadow-md transition-colors"
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15m10.5-8.25h-4.5m4.5 0v4.5m0-4.5L15 9"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default Preview;
